import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Modal,
  ButtonBase,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  Check as CheckIcon,
  SwapHoriz as SwapHorizIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router";
import { Pages } from "../../utils";
import { useGenerateBeforeAndAfterShareTokenMutation } from "../../hooks/use-generate-before-and-after-share-token-mutation";
import { formatBudgetRange, formatSquareFootage, formatProjectDuration } from "../../utils/enum-formatters";
import { formatSpecialRequirementLabel } from "../../utils/option-helpers";
import { useUserContext } from "../../hooks/userContext";
import { useDesignerAvatar } from "../../hooks/useDesignerAvatar";
import { useGetDesignerBioQuery } from "../../hooks/use-get-designer-bio";
import interiorDesignerSeal from "../../assets/designer-files/Interior_designer.jpg";

interface BeforeAfterBlogLayoutProps {
  project: {
    _id: string;
    name: string;
    storyTitle?: string;
    storySubtitle?: string;
    introduction?: string;
    challenges?: string;
    wishList?: string[];
    overview?: string;
    additionalSections?: Array<{
      title: string;
      content: string;
    }>;
    beforeImages?: Array<{
      _id: string;
      url: string;
      title?: string;
      description?: string;
    }>;
    afterImages?: Array<{
      _id: string;
      url: string;
      title?: string;
      description?: string;
    }>;
    coverImage?: {
      _id: string;
      url: string;
      title?: string;
      description?: string;
    } | null;
    style?: string;
    projectCategory?: string;
    budgetRange?: string;
    squareFootage?: string;
    projectDuration?: string;
    specialRequirements?: string[];
    designerId?: {
      _id?: string;
      firstName?: string;
      lastName?: string;
      businessCity?: string;
      businessState?: string;
    } | null;
  };
  onEdit?: () => void;
  showEditButton?: boolean;
  isMember?: boolean;
  showPaywall?: boolean;
  onBack?: () => void;
}

export const BeforeAfterBlogLayout: React.FC<BeforeAfterBlogLayoutProps> = ({
  project,
  onEdit,
  showEditButton = false,
  isMember = false,
  showPaywall = true,
  onBack,
}) => {
  const navigate = useNavigate();
  const { generateShareToken } = useGenerateBeforeAndAfterShareTokenMutation();
  const [shareToken, setShareToken] = useState<string | null>((project as any).shareToken || null);
  const [isNavVisible, setIsNavVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const { user } = useUserContext();
  const { avatarUrl: currentUserAvatar, initials: currentUserInitials } = useDesignerAvatar();
  const { getDesignerBio, designerBio } = useGetDesignerBioQuery();
  const [designerAvatarUrl, setDesignerAvatarUrl] = useState<string | undefined>(undefined);
  const [previewImage, setPreviewImage] = useState<{ url: string; label: string } | null>(null);

  // Determine which designer ID to use
  const projectDesignerId = project.designerId?._id;
  const currentDesignerId = user?.accountType === 'Designer' ? user?.id : null;
  const designerId = projectDesignerId || currentDesignerId;
  const isCurrentUser = designerId === user?.id;

  // Fetch designer bio/avatar if viewing another designer's project
  useEffect(() => {
    if (designerId && !isCurrentUser) {
      getDesignerBio(designerId, false).then((bio) => {
        if (bio?.image?.url) {
          setDesignerAvatarUrl(bio.image.url as string);
        }
      }).catch((error) => {
      });
    } else {
      // Clear designer avatar if viewing current user's project
      setDesignerAvatarUrl(undefined);
    }
  }, [designerId, isCurrentUser, getDesignerBio]);

  useEffect(() => {
    const getScrollParent = (node: HTMLElement | null) => {
      if (!node) return window;
      let current: HTMLElement | null = node.parentElement;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        if (overflowY === "auto" || overflowY === "scroll") {
          return current;
        }
        current = current.parentElement;
      }
      return window;
    };

    const scrollContainer = getScrollParent(rootRef.current);
    const getScrollTop = () =>
      scrollContainer === window
        ? window.scrollY || 0
        : (scrollContainer as HTMLElement).scrollTop;

    lastScrollYRef.current = getScrollTop();

    const handleScroll = () => {
      if (tickingRef.current) return;
      tickingRef.current = true;
      window.requestAnimationFrame(() => {
        const currentY = getScrollTop();
        const lastY = lastScrollYRef.current;
        const delta = currentY - lastY;

        if (currentY < 8) {
          setIsNavVisible(true);
        } else if (delta > 8) {
          setIsNavVisible(false);
        } else if (delta < -6) {
          setIsNavVisible(true);
        }

        lastScrollYRef.current = currentY;
        tickingRef.current = false;
      });
    };

    const target = scrollContainer === window ? window : (scrollContainer as HTMLElement);
    target.addEventListener("scroll", handleScroll, { passive: true });
    return () => target.removeEventListener("scroll", handleScroll);
  }, []);

  // Use appropriate avatar
  const avatarUrl = isCurrentUser ? currentUserAvatar : (designerAvatarUrl || designerBio?.image?.url as string | undefined);

  // Get designer information - prefer from project, fallback to current user
  const designerInfo = useMemo(() => {
    if (project.designerId) {
      return {
        firstName: project.designerId.firstName || '',
        lastName: project.designerId.lastName || '',
        city: project.designerId.businessCity || '',
        state: project.designerId.businessState || '',
      };
    } else if (user?.accountType === 'Designer') {
      return {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        city: (user as any).businessCity || '',
        state: (user as any).businessState || '',
      };
    }
    return null;
  }, [project.designerId, user]);

  const designerName = designerInfo 
    ? `${designerInfo.firstName} ${designerInfo.lastName}`.trim()
    : 'Designer';
  
  const designerInitials = useMemo(() => {
    if (designerInfo) {
      const first = designerInfo.firstName?.trim()?.[0] ?? '';
      const last = designerInfo.lastName?.trim()?.[0] ?? '';
      return `${first}${last}`.trim().toUpperCase() || 'D';
    }
    return isCurrentUser ? (currentUserInitials || 'D') : 'D';
  }, [designerInfo, isCurrentUser, currentUserInitials]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Prefer going back to the previous location (e.g., closing modal overlay)
    // but if there is no history entry, fall back to design files list.
    const canGoBack = (window.history.state?.idx ?? 0) > 0;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate(Pages.MY_DESIGN_FILES, { replace: true });
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
    } else {
      navigate(Pages.ADD_DESIGNER_FILE__BEFORE_AFTER);
    }
  };

  const handleShare = async () => {
    try {
      // Generate share token if it doesn't exist
      let currentShareToken = shareToken;
      if (!currentShareToken) {
        const result = await generateShareToken({
          variables: { projectId: project._id },
        });
        currentShareToken = result.data?.generateBeforeAndAfterShareToken?.shareToken || null;
        setShareToken(currentShareToken);
      }

      // Use share token if available, otherwise fall back to project ID
      const shareIdentifier = currentShareToken || project._id;
      const shareUrl = `${window.location.origin}/shared/before-after/${shareIdentifier}`;
      const shareText = `Check out this amazing ${project.projectCategory || 'design'} transformation by a professional designer on Home Design Guild! 🏠✨`;
      
      // Create a teaser description for social media
      const teaserDescription = project.storyTitle 
        ? `"${project.storyTitle}" - See the incredible before & after transformation!`
        : `Amazing ${project.projectCategory || 'design'} transformation - Before & After!`;
      
      const fullShareText = `${shareText}\n\n${teaserDescription}\n\n🔒 Full project details available to Home Design Guild members\n💎 Join to see all designer portfolios and get matched with your perfect designer!\n\n${shareUrl}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: `${project.storyTitle || project.name} - Home Design Guild`,
            text: fullShareText,
            url: shareUrl,
          });
        } catch (error) {
          // Fallback to clipboard
          await navigator.clipboard.writeText(fullShareText);
          alert('Share link copied to clipboard!');
        }
      } else {
        // Fallback for browsers that don't support Web Share API
        await navigator.clipboard.writeText(fullShareText);
        alert('Share link copied to clipboard!');
      }
    } catch (error) {
      alert('Failed to generate share link. Please try again.');
    }
  };

  const handleFavorite = () => {
    // TODO: Implement favorite functionality
  };

  const handleOpenPreview = (url: string, label: string) => {
    setPreviewImage({ url, label });
  };

  const handleClosePreview = () => {
    setPreviewImage(null);
  };

  const beforeAfterPairs = useMemo(() => {
    const maxCount = Math.max(project.beforeImages?.length || 0, project.afterImages?.length || 0);
    return Array.from({ length: maxCount }).map((_, index) => ({
      before: project.beforeImages?.[index],
      after: project.afterImages?.[index],
    })).filter((pair) => pair.before?.url || pair.after?.url);
  }, [project.beforeImages, project.afterImages]);
  const wishList = project.wishList ?? [];

  const RichTextBlock = ({ html }: { html: string }) => (
    <Box
      sx={{
        lineHeight: 1.9,
        fontSize: "1rem",
        color: "#4A4A4A",
        "& img": {
          maxWidth: "100%",
          height: "auto",
          display: "block",
          margin: "24px auto",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
        "& p": {
          marginBottom: "16px",
        },
        "& h1, & h2, & h3, & h4": {
          fontFamily: "'Cormorant Garamond', serif",
          fontWeight: 600,
          letterSpacing: "-0.01em",
          marginBottom: "12px",
          color: "#1B1B1B",
        },
        "& ul, & ol": {
          paddingLeft: "24px",
          marginBottom: "16px",
        },
        "& li": {
          marginBottom: "8px",
        },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );

  const stripHtml = (value = "") =>
    value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

  type DetailValue =
    | { label: string; value: string; isChips: false }
    | { label: string; value: string[]; isChips: true };

  type DetailRow = {
    left: DetailValue;
    right: DetailValue;
  };

  const detailRows = useMemo<DetailRow[]>(() => {
    const formatTitleCase = (value?: string | null) =>
      value ? value.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()).trim() : "";

    const specialRequirements = project.specialRequirements?.length
      ? project.specialRequirements.map((req) => formatSpecialRequirementLabel(req))
      : [];

    const rows = [
      {
        left: {
          label: "Style",
          value: formatTitleCase(project.style),
          isChips: false,
        },
        right: {
          label: "Category",
          value: formatTitleCase(project.projectCategory),
          isChips: false,
        },
      },
      {
        left: {
          label: "Budget Range",
          value: project.budgetRange ? formatBudgetRange(project.budgetRange) : "",
          isChips: false,
        },
        right: {
          label: "Square Footage",
          value: project.squareFootage ? formatSquareFootage(project.squareFootage) : "",
          isChips: false,
        },
      },
      {
        left: {
          label: "Duration",
          value: project.projectDuration ? formatProjectDuration(project.projectDuration) : "",
          isChips: false,
        },
        right: {
          label: "Special Requirements",
          value: specialRequirements,
          isChips: true,
        },
      },
    ] as DetailRow[];

    return rows.filter((row) => {
      const leftHas = row.left.isChips ? row.left.value.length > 0 : Boolean(row.left.value);
      const rightHas = row.right.isChips ? row.right.value.length > 0 : Boolean(row.right.value);
      return leftHas || rightHas;
    });
  }, [
    project.style,
    project.projectCategory,
    project.budgetRange,
    project.squareFootage,
    project.projectDuration,
    project.specialRequirements,
  ]);

  const introductionSection = useMemo(() => {
    const html = project.introduction || "";
    if (!html.trim() || typeof DOMParser === "undefined") {
      return { heading: null, body: html };
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    const headingEl = doc.querySelector("h1, h2, h3");
    const heading = headingEl?.textContent?.trim() || null;
    if (headingEl) {
      headingEl.remove();
    }
    return { heading, body: doc.body.innerHTML || "" };
  }, [project.introduction]);

  const challengesSection = useMemo(() => {
    const html = project.challenges || "";
    if (!html.trim() || typeof DOMParser === "undefined") {
      return { heading: null, body: html, imageUrl: null };
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    const headingEl = doc.querySelector("h1, h2, h3");
    const heading = headingEl?.textContent?.trim() || null;
    if (headingEl) {
      headingEl.remove();
    }

    const challengeImageEl = doc.querySelector('img[data-challenge-image="true"]');
    const imageUrl = challengeImageEl?.getAttribute("src") || null;
    if (challengeImageEl) {
      const parent = challengeImageEl.parentElement;
      challengeImageEl.remove();
      if (parent && parent.tagName.toLowerCase() === "p" && !parent.textContent?.trim()) {
        parent.remove();
      }
    }

    return { heading, body: doc.body.innerHTML || "", imageUrl };
  }, [project.challenges]);

  const BeforeAfterSlider = ({
    beforeUrl,
    afterUrl,
    label,
  }: {
    beforeUrl: string;
    afterUrl: string;
    label?: string;
  }) => {
    const [position, setPosition] = useState(50);

    return (
      <Box
        sx={{
          position: "relative",
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: "16px 16px 0 0",
          overflow: "hidden",
          backgroundColor: "#E6E6E6",
        }}
      >
        <Box
          component="img"
          src={afterUrl}
          alt={label ? `${label} after` : "After"}
          sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            overflow: "hidden",
            clipPath: `inset(0 ${100 - position}% 0 0)`,
            WebkitClipPath: `inset(0 ${100 - position}% 0 0)`,
          }}
        >
          <Box
            component="img"
            src={beforeUrl}
            alt={label ? `${label} before` : "Before"}
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${position}%`,
            width: "2px",
            backgroundColor: "#fff",
            transform: "translateX(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: `${position}%`,
            transform: "translate(-50%, -50%)",
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 18, color: "#111" }} />
        </Box>
        <Box
          component="input"
          type="range"
          min={0}
          max={100}
          value={position}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setPosition(Number(event.target.value))
          }
          aria-label="Before and after comparison slider"
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "ew-resize",
          }}
        />
      </Box>
    );
  };

  const sectionLabelSx = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    mb: 1.5,
  };

  const sectionLabelTextSx = {
    fontWeight: 700,
    letterSpacing: "0.2em",
    fontSize: "0.75rem",
    color: "#7A7A7A",
  };

  const sectionDividerSx = {
    flex: 1,
    height: "1px",
    backgroundColor: "#E6E6E6",
  };

  const sectionHeadingSx = {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 600,
    fontSize: { xs: "2rem", md: "2.6rem" },
    letterSpacing: "-0.01em",
    color: "#1B1B1B",
    mb: 2,
  };

  return (
    <Box ref={rootRef} sx={{ minHeight: "100vh", backgroundColor: "#fafafa" }}>
      {/* Header with navigation */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: "white",
          py: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          transition: "opacity 0.25s ease, transform 0.25s ease",
          opacity: isNavVisible ? 1 : 0,
          transform: isNavVisible ? "translateY(0)" : "translateY(-12px)",
          pointerEvents: isNavVisible ? "auto" : "none",
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ color: "text.secondary" }}
            >
              Back
            </Button>
            <Stack direction="row" spacing={1}>
              {showEditButton && (
                <Button variant="outlined" onClick={handleEdit}>
                  Edit Project
                </Button>
              )}
              <IconButton onClick={handleShare} size="small">
                <ShareIcon />
              </IconButton>
              <IconButton onClick={handleFavorite} size="small">
                <FavoriteIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: "white" }}>
        <Container maxWidth="lg">
          <Stack spacing={4}>
            {/* Project Title */}
            <Box textAlign="center">
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontWeight: 600,
                  fontSize: { xs: "2.4rem", md: "3.4rem" },
                  lineHeight: 1.2,
                  mb: 1.5,
                  letterSpacing: "-0.02em",
                  color: "#1B1B1B",
                }}
              >
                {project.storyTitle || project.name}
              </Typography>
              {project.storySubtitle && (
                <Typography
                  variant="h5"
                  sx={{
                    color: "#5A5A5A",
                    fontWeight: 400,
                    maxWidth: "720px",
                    mx: "auto",
                    fontSize: { xs: "1.1rem", md: "1.35rem" },
                  }}
                >
                  {project.storySubtitle}
                </Typography>
              )}
            </Box>

            {/* Project Details */}
            <Box>
              <Grid container spacing={2} justifyContent="center">
                    {project.style && (
                      <Grid item>
                        <Chip
                          label={`Style: ${project.style?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}`}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderRadius: "999px",
                            px: 1.5,
                            borderColor: "#D7DCE1",
                            color: "#3D3D3D",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Grid>
                    )}
                    {project.projectCategory && (
                      <Grid item>
                        <Chip
                          label={`Category: ${project.projectCategory?.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim()}`}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderRadius: "999px",
                            px: 1.5,
                            borderColor: "#D7DCE1",
                            color: "#3D3D3D",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Grid>
                    )}
                    {project.budgetRange && (
                      <Grid item>
                        <Chip
                          label={`Budget: ${formatBudgetRange(project.budgetRange)}`}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderRadius: "999px",
                            px: 1.5,
                            borderColor: "#D7DCE1",
                            color: "#3D3D3D",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Grid>
                    )}
                    {project.squareFootage && (
                      <Grid item>
                        <Chip
                          label={`Size: ${formatSquareFootage(project.squareFootage)}`}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderRadius: "999px",
                            px: 1.5,
                            borderColor: "#D7DCE1",
                            color: "#3D3D3D",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Grid>
                    )}
                    {project.projectDuration && (
                      <Grid item>
                        <Chip
                          label={`Duration: ${formatProjectDuration(project.projectDuration)}`}
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            borderRadius: "999px",
                            px: 1.5,
                            borderColor: "#D7DCE1",
                            color: "#3D3D3D",
                            backgroundColor: "#fff",
                          }}
                        />
                      </Grid>
                    )}
              </Grid>
            </Box>

            {/* Cover Image */}
            {project.coverImage && (
              <Box sx={{ mt: 4 }}>
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "21 / 9",
                    borderRadius: "18px",
                    overflow: "hidden",
                    backgroundColor: "#E6E6E6",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
                  }}
                >
                  <Box
                    component="img"
                    src={project.coverImage.url}
                    alt={project.storyTitle || project.name}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Paywall Teaser Section - Only show for non-members */}
      {!isMember && showPaywall && (
        <Box sx={{ py: 6, backgroundColor: "#f8f9fa" }}>
          <Container maxWidth="md">
            <Box
              sx={{
                textAlign: "center",
                p: 4,
                backgroundColor: "white",
                borderRadius: 3,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                border: "2px solid #e0e0e0",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}
              >
                🔒 Exclusive Content
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 3, color: "text.secondary" }}
              >
                Full project details and designer portfolio are available to Home Design Guild members
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 4, color: "text.primary", lineHeight: 1.6 }}
              >
                Get access to complete project stories, designer portfolios, and personalized matching with professional designers who fit your style and budget.
              </Typography>
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                <Chip
                  icon={<span>🏠</span>}
                  label="Unlimited Designer Access"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<span>💎</span>}
                  label="Premium Matching"
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  icon={<span>📱</span>}
                  label="Mobile App Access"
                  color="primary"
                  variant="outlined"
                />
              </Stack>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "#1976d2",
                  color: "white",
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  borderRadius: 2,
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
                onClick={() => {
                  // Navigate to membership signup
                  window.open('/membership', '_blank');
                }}
              >
                Join Home Design Guild - $4.99/month
              </Button>
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 2, color: "text.secondary" }}
              >
                Cancel anytime • 7-day free trial
              </Typography>
            </Box>
          </Container>
        </Box>
      )}

      {/* Story Content - Only show for members */}
      {isMember && (
        <Box sx={{ py: 6, backgroundColor: "white" }}>
          <Container maxWidth="md">
            <Stack spacing={6}>
            {/* Introduction */}
            {project.introduction && (
              <Box>
                <Box sx={sectionLabelSx}>
                  <Typography sx={sectionLabelTextSx}>INTRODUCTION</Typography>
                  <Box sx={sectionDividerSx} />
                </Box>
                {/* <Typography
                  variant="h4"
                  sx={{
                    ...sectionHeadingSx,
                    mb: 1.5,
                  }}
                >
                  {introductionSection.heading || "Introduction"}
                </Typography> */}
                <RichTextBlock html={introductionSection.body} />
              </Box>
            )}

            {/* Challenges */}
            {project.challenges && (
              <Box>
                <Box sx={sectionLabelSx}>
                  <Typography sx={sectionLabelTextSx}>CHALLENGES</Typography>
                  <Box sx={sectionDividerSx} />
                </Box>
                {challengesSection.imageUrl && (
                  <Box
                    sx={{
                      width: "100%",
                      aspectRatio: "4 / 3",
                      borderRadius: "16px",
                      overflow: "hidden",
                      backgroundColor: "#E6E6E6",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                      mb: 3,
                    }}
                  >
                    <Box
                      component="img"
                      src={challengesSection.imageUrl}
                      alt="Challenge"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                )}
                {/* <Typography
                  variant="h4"
                  sx={{
                    ...sectionHeadingSx,
                    mb: 1.5,
                  }}
                >
                  {challengesSection.heading || "Challenges"}
                </Typography> */}
                <RichTextBlock html={challengesSection.body} />
              </Box>
            )}

            {/* Wish List */}
            {wishList.length ? (
              <Box>
                <Box
                  sx={{
                    backgroundColor: "#F4F8FA",
                    borderRadius: "16px",
                    p: { xs: 2, md: 3 },
                  }}
                >
                  <Typography sx={{ ...sectionLabelTextSx, color: "#1B1B1B" }}>
                    CLIENT WISH LIST
                  </Typography>
                  <Box
                    sx={{
                      mt: 2,
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      overflow: "hidden",
                      border: "1px solid #E9EEF2",
                    }}
                  >
                    {wishList.map((item, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          px: 2.5,
                          py: 1.75,
                          borderBottom:
                            index === wishList.length - 1 ? "none" : "1px solid #E9EEF2",
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 18, color: "#1B1B1B" }} />
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : null}

            {/* Before & After */}
            {(project.overview || beforeAfterPairs.length > 0) && (
              <Box>
                <Box sx={sectionLabelSx}>
                  <Typography sx={sectionLabelTextSx}>TRANSFORMATION</Typography>
                  <Box sx={sectionDividerSx} />
                </Box>
                <Typography variant="h4" sx={sectionHeadingSx}>
                  Before &amp; After
                </Typography>
                {project.overview && (
                  <Box sx={{ mb: 3 }}>
                    <RichTextBlock html={project.overview} />
                  </Box>
                )}
                <Stack spacing={4}>
                  {beforeAfterPairs
                    .filter((pair) => pair.before?.url && pair.after?.url)
                    .map((pair, index) => {
                      const descriptionHtml = pair.before?.description || pair.after?.description || "";
                      const hasDescription = !!stripHtml(descriptionHtml);
                      const title = pair.before?.title || pair.after?.title;

                      return (
                        <Card
                          key={`${pair.before?._id || "before"}-${pair.after?._id || "after"}-${index}`}
                          sx={{
                            borderRadius: "16px",
                            overflow: "hidden",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                          }}
                        >
                          <BeforeAfterSlider
                            beforeUrl={pair.before?.url as string}
                            afterUrl={pair.after?.url as string}
                            label={title || `Pair ${index + 1}`}
                          />
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              borderTop: "1px solid #E9EEF2",
                              borderBottom: (title || hasDescription) ? "1px solid #E9EEF2" : "none",
                              backgroundColor: "#fff",
                            }}
                          >
                            <Stack direction="row" spacing={1.5}>
                              {[
                                { url: pair.before?.url, label: "Before" },
                                { url: pair.after?.url, label: "After" },
                              ]
                                .filter((item) => item.url)
                                .map((item) => (
                                  <ButtonBase
                                    key={item.label}
                                    onClick={() => handleOpenPreview(item.url as string, item.label)}
                                    sx={{
                                      borderRadius: "8px",
                                      overflow: "hidden",
                                      border: "1px solid #E1E5EA",
                                      width: 96,
                                      height: 64,
                                      position: "relative",
                                      backgroundColor: "#F4F6F8",
                                    }}
                                  >
                                    <Box
                                      component="img"
                                      src={item.url as string}
                                      alt={`${item.label} preview`}
                                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: 4,
                                        left: 4,
                                        px: 0.75,
                                        py: 0.1,
                                        borderRadius: "6px",
                                        backgroundColor: "rgba(0,0,0,0.65)",
                                        color: "#fff",
                                        fontSize: "0.65rem",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {item.label}
                                    </Box>
                                  </ButtonBase>
                                ))}
                            </Stack>
                          </Box>
                          {(title || hasDescription) && (
                            <CardContent sx={{ p: 3 }}>
                              {title && (
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {title}
                                </Typography>
                              )}
                              {hasDescription && (
                                <Box
                                  sx={{
                                    color: "text.secondary",
                                    fontSize: "0.95rem",
                                    "& p": { marginBottom: "6px" },
                                    "& h1, & h2, & h3, & h4": {
                                      fontSize: "1rem",
                                      fontWeight: 600,
                                      marginBottom: "6px",
                                    },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: descriptionHtml }}
                                />
                              )}
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                </Stack>
              </Box>
            )}

            {/* Additional Sections */}
            {project.additionalSections?.map((section, index) => (
              <Box key={index}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  {section.title}
                </Typography>
                <Box
                  sx={{
                    lineHeight: 1.7,
                    fontSize: "1.1rem",
                    color: "text.primary",
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      display: "block",
                      margin: "24px auto",
                      borderRadius: "12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    },
                    "& p": {
                      marginBottom: "16px",
                    },
                    "& ul, & ol": {
                      paddingLeft: "24px",
                      marginBottom: "16px",
                    },
                    "& li": {
                      marginBottom: "8px",
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: section.content }}
                />
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>
      )}

      {/* Project Details Summary */}
      <Box sx={{ py: 6, backgroundColor: "#f6f7f9" }}>
        <Container maxWidth="md">
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: "20px",
              boxShadow: "0 12px 28px rgba(0,0,0,0.08)",
              p: { xs: 3, md: 4 },
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "#1B1B1B",
                mb: 1,
              }}
            >
              Project Details
            </Typography>

            {detailRows.map((row, rowIndex) => (
              <Box
                key={rowIndex}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                  columnGap: { xs: 2, md: 6 },
                  rowGap: 2,
                  py: 3,
                  borderBottom: rowIndex === detailRows.length - 1 ? "none" : "1px solid #ECEFF3",
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: "0.85rem", color: "#6B6B6B", mb: 1 }}>
                    {row.left.label}
                  </Typography>
                  {row.left.isChips ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {row.left.value.map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          sx={{
                            backgroundColor: "#E8E6FF",
                            color: "#4F46E5",
                            fontWeight: 600,
                            borderRadius: "999px",
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: "#4F46E5", fontWeight: 600 }}>
                      {row.left.value as string}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "0.85rem", color: "#6B6B6B", mb: 1 }}>
                    {row.right.label}
                  </Typography>
                  {row.right.isChips ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {row.right.value.map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          size="small"
                          sx={{
                            backgroundColor: "#E8E6FF",
                            color: "#4F46E5",
                            fontWeight: 600,
                            borderRadius: "999px",
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography sx={{ color: "#4F46E5", fontWeight: 600 }}>
                      {row.right.value as string}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid #ECEFF3",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
                gap: 3,
              }}
            >
              {designerInfo && (
                <Stack direction="row" spacing={2} alignItems="center">
                  {avatarUrl ? (
                    <Box
                      component="img"
                      src={avatarUrl}
                      alt={designerName}
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "50%",
                        backgroundColor: "#E8E6FF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#4F46E5",
                        fontWeight: 600,
                      }}
                    >
                      {designerInitials}
                    </Box>
                  )}
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "'Cormorant Garamond', serif",
                        fontWeight: 600,
                        fontSize: "1.1rem",
                        color: "#1B1B1B",
                      }}
                    >
                      {designerName}
                    </Typography>
                    <Typography
                      sx={{
                        textTransform: "uppercase",
                        letterSpacing: "0.2em",
                        fontSize: "0.7rem",
                        color: "#7A7A7A",
                        fontWeight: 600,
                      }}
                    >
                      Principal Designer
                    </Typography>
                  </Box>
                </Stack>
              )}
              <Box
                sx={{
                  textAlign: { xs: "left", md: "right" },
                  marginLeft: { md: "auto" },
                }}
              >
                <Box
                  component="img"
                  src={interiorDesignerSeal}
                  alt="Home Design Guild Seal of Approval"
                  sx={{
                    width: { xs: 180, md: 210 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />
                <Typography sx={{ mt: 1, fontSize: "0.75rem", color: "#7A7A7A" }}>
                  Home Design Guild Seal of Approval
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      <Modal
        open={Boolean(previewImage)}
        onClose={handleClosePreview}
        aria-labelledby="before-after-full-image"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
        BackdropProps={{
          sx: { backgroundColor: "rgba(0,0,0,0.75)" },
        }}
      >
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            position: "relative",
            maxWidth: "92vw",
            maxHeight: "92vh",
            outline: "none",
          }}
        >
          <IconButton
            onClick={handleClosePreview}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
              zIndex: 2,
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage.url}
              alt={previewImage.label}
              sx={{
                display: "block",
                maxWidth: "92vw",
                maxHeight: "92vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
                borderRadius: "10px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
                backgroundColor: "#111",
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default BeforeAfterBlogLayout;
